import { useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight, Check } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../ui/dialog'
import PaintedCard from './PaintedCard'
import type { PaintedWords } from './PaintedCard'
import PaintedColourRows from './PaintedColourRows'
import { categoryLabel } from '../../lib/inviteTemplates'
import type { InviteTemplate } from '../../lib/inviteTemplates'
import { paintedLookLabel, resolvePaintedChoice } from '../../lib/paintedInvite'

/**
 * A large, animated look at one painted template — with the host's own words
 * when they have typed any — plus its colour rows and "Use this design".
 * The arrows (and ← / →) step through whatever list the caller is showing, so
 * a host can page through the collection without leaving the dialog.
 */

type Props = {
  templateId: string | null
  list: InviteTemplate[]
  words: PaintedWords
  lang: string
  appLang: string
  /** The host's current choice, so reopening their own template keeps its colours. */
  current?: { template?: string; accent?: string; background?: string; font?: string }
  onClose: () => void
  onNavigate: (id: string) => void
  onUse: (patch: { template: string; accent: string; background: string }) => void
  useLabel?: string
}

const PaintedPreviewDialog = ({ templateId, list, words, lang, appLang, current, onClose, onNavigate, onUse, useLabel }: Props) => {
  const t = (en: string, ka: string) => (appLang === 'ka' ? ka : en)
  const index = list.findIndex((tpl) => tpl.id === templateId)
  const template = index >= 0 ? list[index] : null
  const def = template?.painted || null

  const [accent, setAccent] = useState('')
  const [background, setBackground] = useState('')
  useEffect(() => {
    const own = current?.template === templateId
    setAccent(own ? current?.accent || '' : '')
    setBackground(own ? current?.background || '' : '')
  }, [templateId, current?.template, current?.accent, current?.background])

  const step = (delta: number) => {
    if (!list.length || index < 0) return
    onNavigate(list[(index + delta + list.length) % list.length].id)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if ((e.target as HTMLElement).tagName === 'INPUT') return
    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      step(-1)
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      step(1)
    }
  }

  const arrow =
    'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#1b1033]/12 bg-white text-[#1b1033] transition-colors hover:border-[#ff4d8d] hover:text-[#ff4d8d] dark:border-white/15 dark:bg-white/5 dark:text-[#f4f0ff]'

  return (
    <Dialog open={!!def} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-h-[94dvh] max-w-[min(980px,calc(100%-1.5rem))] overflow-y-auto border-0 bg-[#fff7f2] p-0 dark:bg-[#130b26] sm:rounded-2xl"
        onKeyDown={onKeyDown}
        data-testid="painted-preview-dialog"
      >
        {def && template && (
          <div className="grid gap-0 md:grid-cols-[auto_minmax(0,1fr)]">
            <div className="flex items-center justify-center bg-[#1b1033]/[0.06] p-4 dark:bg-black/30 sm:p-6">
              <div
                className="overflow-hidden rounded-xl shadow-[0_30px_60px_-24px_rgba(27,16,51,0.55)]"
                style={{ width: 'min(78vw, calc(min(76dvh, 760px) * 2 / 3))' }}
              >
                <PaintedCard
                  key={`${def.id}-${lang}`}
                  def={def}
                  choice={resolvePaintedChoice(def, accent, background)}
                  words={words}
                  lang={lang}
                  font={current?.template === templateId ? current?.font : null}
                  play
                />
              </div>
            </div>
            <div className="flex flex-col gap-5 p-5 sm:p-7">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#ff4d8d]">
                  {paintedLookLabel(def.look, appLang)} · {categoryLabel(def.category, appLang)}
                </p>
                <DialogTitle className="mt-1.5 text-2xl font-semibold leading-tight text-[#1b1033] dark:text-[#f4f0ff]">
                  {appLang === 'ka' ? def.label.ka : def.label.en}
                </DialogTitle>
                <DialogDescription className="mt-1.5 text-sm text-[#4a3d66] dark:text-[#c3b8dd]">
                  {appLang === 'ka' ? def.blurb.ka : def.blurb.en}
                </DialogDescription>
              </div>

              <PaintedColourRows
                def={def}
                accent={accent}
                background={background}
                onAccent={setAccent}
                onBackground={setBackground}
                appLang={appLang}
                compact
                testPrefix="painted-preview"
              />

              <div className="mt-auto flex items-center gap-2 pt-2">
                <button type="button" onClick={() => step(-1)} className={arrow} aria-label={t('Previous design', 'წინა დიზაინი')} data-testid="painted-preview-prev">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => step(1)} className={arrow} aria-label={t('Next design', 'შემდეგი დიზაინი')} data-testid="painted-preview-next">
                  <ChevronRight className="h-4 w-4" />
                </button>
                <span className="px-1 text-xs tabular-nums text-[#4a3d66] dark:text-[#c3b8dd]">
                  {index + 1} / {list.length}
                </span>
                <button
                  type="button"
                  onClick={() => onUse({ template: def.id, accent, background })}
                  className="ml-auto inline-flex h-10 items-center gap-1.5 rounded-full bg-[#ff4d8d] px-5 text-sm font-semibold text-white transition-colors hover:bg-[#e63d7c]"
                  data-testid="painted-preview-use"
                >
                  <Check className="h-4 w-4" />
                  {useLabel || t('Use this design', 'ამ დიზაინით')}
                </button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default PaintedPreviewDialog
